import { MAX_SPEED_LIMIT } from "./constants";
import { getDate, getLocalisedDate, getLocalisedTime, secondsToHms } from "./datetime";
import { sphericalCosinus } from "./geodist";

export const getNumberSuffix = (number) => {
  if (number > 3 && number < 21) return "th";
  switch (number % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  };
};

export const getLocalisedNumber = (number) => number.toLocaleString("en-IN");

export const getMean = (set, key, precision = 1) => {
  const total = set.reduce((accumulator, value) => (accumulator + value[key]), 0);
  return (total / (set.length + 1)).toFixed(precision);
};

export const deepClone = (object) => {
  let clone = Array.isArray(object) ? [] : {};

  let value;
  for (const key in object) {
    value = object[key];
    clone[key] = (typeof value === "object") ? deepClone(value) : value;
  }

  return clone;
}

export const ellipsisify = (string, length) => {
  if(!string) return "";
  else if(string.length <= length) return string;

  if(string.length > length) return `${string.substring(0, length)}...`
};

export const getUserAndVehicle = (data) => {
  return data.vehicles.map(vehicle => {
    let user = data.users.find(user => user.idx === vehicle.uid);
    let stat = data.stats.find(stat => stat.vid === vehicle.idx);
    if(user) vehicle["user"] = user;
    if(stat) vehicle["stat"] = stat;

    return vehicle;
  });
};

export const getDateTimeRows = (thresholdCrossedTimes) => {
  return (
    thresholdCrossedTimes.map(crossedTs => {
      return `${getDate(crossedTs, true, "/")} ${getLocalisedTime(crossedTs)}`;
    })
  );
};

const shortenNumber = (num) => {
  if (num >= 100000) {
    return (num / 100000).toFixed(1).replace(/\.0$/, "") + "L";
  }

  /*
  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, "") + "k";
  }
  */

  return num;
};

/**
 * Gets Formatted Value with Units for a Stat
 * @param {Number} value                              - Raw Value for the Statistic
 * @param {('distance'|'duration'|'speed'|'time')} statType  - Type of Given Statistic
 * @param {Number} precision                          - Precision in Output
 * @param {Boolean} long                              - Prevent using abbreviations
 * @returns
 */
export const getStatLabel = (value, statType, precision, long, round) => {
  const getPlural = (val) => (val !== 1 ? "s" : "");
  const toKilometers = (val) => (val / 1000).toFixed(precision ?? (long ? 2 : 0));
  const renderValueWithUnit = (val, unit) => (
    <p>{val}<span>{unit}</span></p>
  );

  switch (statType) {
    case "time":
      return value
        ? getLocalisedTime(value * 1000, long, true, true)
        : "00:00:00";

    case "distance":
      let val = long ? toKilometers(value) : shortenNumber(toKilometers(value)).toLocaleString();
      return renderValueWithUnit(round ? (Math.floor(val)).toLocaleString() : val.toLocaleString(), " kms");

    case "duration": {
      if (!long) return secondsToHms(value || 0);

      const timeObj = secondsToHms(value, true);
      return (
        <p>
          {<>{(timeObj.h).toLocaleString()}<span> hrs </span></>}
          {/* {timeObj.m}<span> min{getPlural(timeObj.m)}</span> */}
        </p>
      );
    }

    case "speed": {
      if (isNaN(value) || value == null) return "N/A";
      else value = Number(value);

      const display = value < 250 ? (value).toFixed(precision ?? (long ? 1 : 0)) : 250;
      return renderValueWithUnit(Math.round(display).toLocaleString(), " km/h");
    }

    case "date": {
      return getLocalisedDate(value, true);
    }

    default:
      return String(value.toFixed(precision ?? 0));
  }
};

export const getItemFromLocalStorage = (item) => {
  try {
    const storedUser = window.localStorage.getItem(item || "user");
    try{
      return storedUser ? JSON.parse(storedUser) : null;
    }
    catch(e){
      return storedUser ? storedUser : null;
    }
  } catch (error) {
    console.error("Error parsing user data from localStorage:", error);
    return null;
  }
};

export const getFomattedAmt = (amt) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0
    }).format(amt || 0);
  }

export const getRidingStatus = (score) => {
  if (score >= 0 && score <= 35) return "Poor";
  if (score > 35 && score <= 70) return "Average";
  if (score > 70 && score <= 100) return "Excellent";
}

export const downloadFileFromUrl = async (url, filename) => {
  if (!url) return console.error("No URL provided for file download");
  if (!filename) return console.error("No filename provided for file download");

  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Error fetching file: ${response.status} ${response.statusText}`);

    const blob = await response.blob();
    const fileURL = URL.createObjectURL(blob);

    const downloadLink = document.createElement("a");
    downloadLink.href = fileURL;
    downloadLink.download = filename;
    downloadLink.click();

    URL.revokeObjectURL(fileURL);
  } catch (error) {
    console.error("Error downloading file:", error);
  }
};

/**
 * Determine the mobile operating system.
 * This function returns one of 'iOS', 'Android', or 'unknown'.
 *
 * @returns {String}
 */
export const getMobileOperatingSystem = () => {
  var userAgent = navigator.userAgent;

  if (/android/i.test(userAgent)) return "Android";

  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) return "iOS";

  return "unknown";
}

export const tsWithin10Mins = (ts) => {
  let newTs = ts * 1000;
  const now = Date.now();
  const tenMinsAgo = now - (10 * 60 * 1000);
  return ((newTs >= tenMinsAgo) && (newTs <= now));
};

export const filterInvalidSpeedPings = (pings) => {
  var lastValidPing = pings[0];
  return pings.filter((ping, idx) => {
    let prevPing = pings[idx - 1];

    if (!prevPing || (!prevPing.lat && !ping.lat)) return false;
    else {
      if (lastValidPing.lat && ping.lat){
        const distance = sphericalCosinus(ping.lat, ping.lng, lastValidPing.lat, lastValidPing.lng);
        const timeDiff = ping.ts - lastValidPing.ts;
        const speed = ((distance / timeDiff) * (18 / 5));
        const devSpd = ping.spd;
        let {isValid, reqavs} = canVehicleCover(distance/1000, timeDiff, MAX_SPEED_LIMIT, ping,lastValidPing );
        if (!isValid && reqavs > MAX_SPEED_LIMIT) lastValidPing = ping;

        if ((ping.hasOwnProperty("motion") && !ping.motion && ping.spd < 10) || (speed >= MAX_SPEED_LIMIT || devSpd >= MAX_SPEED_LIMIT) || (devSpd == 0 && speed > 20) || !isValid) return false;
        if (ping.hasOwnProperty("motion") && pings[idx] && pings[idx + 1] && pings[idx - 1]) {
          var motion_avail = checkMotion(pings[idx - 1], pings[idx], pings[idx + 1])
          if (motion_avail) lastValidPing = ping;
          return motion_avail;
        }

        return true;
      }

      if (!lastValidPing.lat && prevPing.lat) lastValidPing = prevPing;
      return false;
    }
  });
}

const canVehicleCover = (distanceKm, durationSec, maxSpeedKph, currPing,lastValidPing) => {
  const { spd: currSpd, ts } = currPing;
  const timeHours = durationSec / 3600;
  const requiredAvgSpeed = distanceKm / timeHours;

  let res = Math.abs(currSpd - requiredAvgSpeed) < 30 && requiredAvgSpeed <= maxSpeedKph;
  return { isValid: res, reqavs: requiredAvgSpeed };
}

const checkMotion = (A, B, C) => {
  if(A.motion && B.motion && C.motion) return true;
  return false;
}

export function transformUserDetails(apiResponse) {
  const settings = apiResponse?.userSettings || {};
  const vehicles = settings.vd || [];
  const glsubs = settings.glsubs || [];
  const gldev = settings.gldev || [];
  const glWarranties = settings.glWarranties || [];
  const subsMap = {};
  glsubs.forEach(s => {
    if (!s.deleted) {
      if (!subsMap[s.did] || s.sed > subsMap[s.did].sed) {
        subsMap[s.did] = s;
      }
    }
  });

  const devMap = {};
  gldev.forEach(d => { devMap[d.idx] = d; });

  const warrantyMap = {};
  glWarranties.forEach(w => { warrantyMap[w.did] = w; });

  return vehicles
    .filter(v => !v.deleted && v.did)
    .map(v => {
      const sub = subsMap[v.did] || {};
      const dev = devMap[v.did] || {};
      const war = warrantyMap[v.did] || {};
      const planMonths = Number(sub.months ?? sub.subs_months);
      const validPlanMonths = Number.isFinite(planMonths) && planMonths > 0
        ? Math.round(planMonths)
        : null;

      const displayName = [v.vmk, v.mo].filter(Boolean).join(' ') || 'Ajjas Go';
      return {
        id: String(v.idx),
        did: v.did,
        imei: dev.imei || '',
        make: v.vmk || '',
        model: v.mo || '',
        displayName,
        regNumber: v.reg || '',
        hwtyp: dev.hwtyp,
        isBlocked: dev.blocked || false,
        subscriptionExpiry: sub.sed
          ? new Date(sub.sed).toISOString().split('T')[0]
          : '',
        warrantyExpiry: war.sed
          ? new Date(war.sed).toISOString().split('T')[0]
          : '',
        currentPlan: validPlanMonths
          ? `${validPlanMonths} month${validPlanMonths === 1 ? '' : 's'}`
          : '—',
        _sub: sub,
        _warranty: war,
        _device: dev,
        _vehicle: v,
      };
    });
}

export function transformPlans(ajjasSubDetails) {
  if (!ajjasSubDetails?.length) return [];
  return ajjasSubDetails.filter(p => p.month >= 6).sort((a, b) => {
    const aCombo = !!a.items;
    const bCombo = !!b.items;
    if (aCombo !== bCombo) return aCombo ? -1 : 1;
    return b.month - a.month;
  }).map(p => {
    const offerAmt = p.offer?.amt;
    const hasOffer = offerAmt != null && offerAmt !== p.amt;
    const currentPrice = hasOffer ? offerAmt : p.amt;
    const mrpPrice = p.mrp;
    const savingsAmt = mrpPrice ? mrpPrice - Math.round(currentPrice / 1.18) : 0;
    const isCombo = !!p.items;

    return {
      id: String(p.idx),
      idx: p.idx,
      title: isCombo ? 'Super Combo Plan' : p.nm,
      duration: `Validity: ${p.month} month${p.month > 1 ? 's' : ''}`,
      durationMonths: p.month,
      price: currentPrice,
      originalPrice: mrpPrice,
      features: isCombo
        ? ['App & SIM recharge', 'Extended Warranty']
        : ['App & SIM recharge'],
      isPopular: isCombo,
      savings: savingsAmt > 0 ? `Save ₹${savingsAmt}` : undefined,
      subtext: isCombo ? '95% users choose this plan' : undefined,
      _raw: p,
    };
  });
}

export function getDaysLeft(expiryDate) {
  if (!expiryDate) return -999;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const exp = new Date(expiryDate);
  exp.setHours(0, 0, 0, 0);
  return Math.ceil((exp.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

export function getStatusPill(daysLeft, options = {}) {
  const showBlocked = options.showBlocked !== false;
  if (daysLeft <= -999) return { text: 'No data', color: 'red' };
  if (daysLeft > 15) return { text: `Expiring in ${daysLeft} days`, color: 'green' };
  if (daysLeft > 0) return { text: `Expiring in ${daysLeft} days`, color: 'orange' };
  if (daysLeft === 0) return { text: 'Expiring today', color: 'red' };
  if (showBlocked && daysLeft <= -90) return { text: 'Blocked', color: 'red' };
  return { text: `Expired ${Math.abs(daysLeft)} days ago`, color: 'red' };
}
